import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MdClose, MdTimer, MdWarning } from 'react-icons/md';
import { useTenant } from '../context/TenantContext';

export default function TrialBanner() {
  const { club } = useTenant();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('trial_banner_dismissed') === '1'
  );

  if (!club) return null;

  const { status } = club;
  const isTrialing = status === 'trial' || status === 'trialing';
  const isExpired  = status === 'trial_expired';

  if (!isTrialing && !isExpired) return null;

  // daysUntilTrialEnds is returned by the getBySlug API — use it directly.
  // Fall back to date math only if the club object has trialEndsAt (e.g. right
  // after self-signup when setClubDirectly is called with the full response).
  // If neither is available, hide the banner — we can't determine state safely.
  let daysLeft;
  if (typeof club.daysUntilTrialEnds === 'number') {
    daysLeft = club.daysUntilTrialEnds;
  } else if (club.trialEndsAt) {
    daysLeft = Math.max(0, Math.ceil(
      (new Date(club.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)
    ));
  } else {
    return null; // can't determine trial state — hide rather than show wrong message
  }

  // Case 1: trial is active and more than 3 days remain — no banner
  if (isTrialing && daysLeft > 3) return null;

  // Case 3: trial has actually expired (status or days exhausted)
  const showExpired = isExpired || daysLeft === 0;

  if (showExpired) {
    // Expired banner: no dismiss — admin must act
    return (
      <div className="w-full border-b px-4 py-2.5 flex items-center gap-3 bg-red-50 border-red-200">
        <MdWarning className="text-lg flex-shrink-0 text-red-700" />
        <p className="text-sm font-medium flex-1 text-red-800">
          Your free trial has expired.{' '}
          <strong>Upgrade now</strong> to restore access.
        </p>
        <Link
          to="/admin/subscription"
          className="text-xs font-semibold px-3 py-1 rounded-lg bg-red-600 text-white border border-red-600 hover:bg-red-700 transition-colors flex-shrink-0"
        >
          Upgrade Now
        </Link>
      </div>
    );
  }

  // Case 2: trial active, 3 or fewer days left — yellow warning (dismissible)
  if (dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem('trial_banner_dismissed', '1');
    setDismissed(true);
  };

  return (
    <div className="w-full border-b px-4 py-2.5 flex items-center gap-3 bg-yellow-50 border-yellow-200">
      <MdTimer className="text-lg flex-shrink-0 text-yellow-700" />
      <p className="text-sm font-medium flex-1 text-yellow-800">
        Your trial ends in{' '}
        <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong>{' '}
        — upgrade to keep access.
      </p>
      <Link
        to="/admin/subscription"
        className="text-xs font-semibold px-3 py-1 rounded-lg border text-yellow-800 bg-white border-yellow-300 hover:bg-yellow-50 transition-colors flex-shrink-0"
      >
        Upgrade Now
      </Link>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-1 rounded hover:bg-black/5 transition-colors text-yellow-700"
        aria-label="Dismiss"
      >
        <MdClose className="text-base" />
      </button>
    </div>
  );
}
